/**
 * WeightsEditor — how the ranker scores survivors. Each row is a metric + a
 * 0..1 weight (slider + number) + a "lower is better" toggle (membership in
 * `minimize`). A live sum indicator turns amber when the total strays from 1.0;
 * Normalize rescales to 1.0. Order is irrelevant — add / remove only.
 */
import { normalizeWeightRows, weightSum } from './flowModel.js'
import { RowControls } from './RowControls.jsx'
import { MetricPicker } from './MetricPicker.jsx'
import { FlowHelp } from './FlowHelp.jsx'

let _seq = 0

export function WeightsEditor({ rows, onChange, metrics, readOnly = false }) {
  // `readOnly` is set when the flow was imported from a MATLAB script: the
  // weights are MS's, and a second editable copy of his numbers is exactly the
  // drift the import exists to end. The lock is not permanent — FlowEditor
  // offers "Edit anyway" and says the next import wins.
  const update = (i, patch) =>
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)))

  const sum = weightSum(rows)
  const sumOk = rows.length === 0 || (sum >= 0.95 && sum <= 1.05)

  return (
    <section className="admin-aim-section admin-flow-section">
      <div className="admin-flow-section__head">
        <h3 className="admin-aim-section-title">
          Scoring weights <FlowHelp k="scoringWeights" />
        </h3>
        <div className="admin-flow-weights-actions">
          <span
            className={`admin-flow-sum ${sumOk ? 'is-ok' : 'is-warn'}`}
            title="Weights are re-normalized; ≈1.0 keeps them readable."
          >
            Σ {sum.toFixed(2)}
          </span>
          {!readOnly && (
            <>
              <button
                type="button"
                className="admin-ai-btn admin-ai-btn--ghost"
                disabled={!sum}
                onClick={() => onChange(normalizeWeightRows(rows))}
              >
                Normalize
              </button>
              <button
                type="button"
                className="admin-ai-btn admin-ai-btn--ghost admin-flow-add"
                onClick={() =>
                  onChange([...rows, { id: `w${++_seq}`, metric: '', weight: 0.2, minimize: false }])
                }
              >
                + Add weight
              </button>
            </>
          )}
        </div>
      </div>
      <p className="admin-flow-hint">
        How Ion ranks the cells that pass the hard constraints. Weights are
        re-normalized to sum to 1.00 — hit <strong>Normalize</strong> if Σ drifts.
        Tick <strong>lower = better</strong> <FlowHelp k="lowerBetter" /> for
        metrics where less wins (mass, resistance).
      </p>
      {rows.length === 0 && (
        <p className="admin-aim-empty admin-flow-empty">
          Add at least one weighted metric to rank on.
        </p>
      )}
      <ul className="admin-flow-list" role="list">
        {rows.map((r, i) => (
          <li key={r.id} className="admin-flow-weight-row">
            <MetricPicker
              value={r.metric}
              metrics={metrics}
              disabled={readOnly}
              ariaLabel={`Weight ${i + 1} metric`}
              onChange={(metric) => update(i, { metric })}
            />
            <input
              className="admin-flow-slider"
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={r.weight}
              disabled={readOnly}
              aria-label={`Weight ${i + 1} value`}
              onInput={(e) => update(i, { weight: Number(e.currentTarget.value) })}
            />
            <input
              className="admin-aim-input admin-flow-num"
              type="number"
              min="0"
              max="1"
              step="0.05"
              value={r.weight}
              disabled={readOnly}
              aria-label={`Weight ${i + 1} number`}
              onInput={(e) => update(i, { weight: Number(e.currentTarget.value) })}
            />
            <label className="admin-aim-checkbox admin-flow-min">
              <input
                type="checkbox"
                checked={!!r.minimize}
                disabled={readOnly}
                onChange={(e) => update(i, { minimize: e.currentTarget.checked })}
              />
              <span>lower = better</span>
            </label>
            {!readOnly && (
              <RowControls
                index={i}
                count={rows.length}
                label={`weight ${i + 1}`}
                onRemove={() => onChange(rows.filter((_, idx) => idx !== i))}
              />
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}
